import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  TrendingUp,
  Calendar,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { format, subDays, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TopProjectsCardProps {
  onNavigateToProject?: (projectId: string) => void;
  maxProjects?: number;
  defaultTimePeriod?: TimePeriod;
  showActiveOnly?: boolean;
}

type TimePeriod = 'last30days' | 'last90days' | 'currentMonth' | 'currentQuarter' | 'yearToDate' | 'allTime';

interface ProjectTimeData {
  projectId: string;
  projectName: string;
  projectCode: string;
  totalHours: number;
  percentage: number;
  isActive: boolean;
  billable: boolean;
}

export function TopProjectsCard({ 
  onNavigateToProject,
  maxProjects = 5,
  defaultTimePeriod = 'last30days',
  showActiveOnly = false
}: TopProjectsCardProps) {
  const { timesheets, currentEmployee, projects } = useTimesheetForScreen('dashboard');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(defaultTimePeriod);
  const [lastUpdated] = useState(new Date());

  const projectTimeData = useMemo(() => {
    // Calculate date range based on selected period
    const now = new Date();
    let startDate: Date;

    switch (timePeriod) {
      case 'last30days':
        startDate = subDays(now, 30);
        break;
      case 'last90days':
        startDate = subDays(now, 90);
        break;
      case 'currentMonth':
        startDate = startOfMonth(now);
        break;
      case 'currentQuarter':
        startDate = startOfQuarter(now);
        break;
      case 'yearToDate':
        startDate = startOfYear(now);
        break;
      case 'allTime':
        startDate = new Date(0); // Beginning of time
        break;
      default:
        startDate = subDays(now, 30);
    }

    // Filter timesheets for current user and date range
    const userTimesheets = timesheets.filter(ts => 
      ts.employeeId === currentEmployee.id &&
      ts.status === 'approved' && // Only count approved timesheets
      ts.weekEnding >= startDate
    );

    // Calculate total hours per project
    const projectHoursMap = new Map<string, number>();

    userTimesheets.forEach(timesheet => {
      timesheet.entries.forEach(entry => {
        const totalHours = Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
        const currentTotal = projectHoursMap.get(entry.projectId) || 0;
        projectHoursMap.set(entry.projectId, currentTotal + totalHours);
      });
    });

    // Calculate total hours across all projects
    const totalHours = Array.from(projectHoursMap.values()).reduce((sum, hours) => sum + hours, 0);

    // Build project data array
    const projectData: ProjectTimeData[] = Array.from(projectHoursMap.entries())
      .map(([projectId, hours]) => {
        const project = projects.find(p => p.id === projectId);
        return {
          projectId,
          projectName: project?.name || 'Unknown Project',
          projectCode: project?.code || 'N/A',
          totalHours: hours,
          percentage: totalHours > 0 ? (hours / totalHours) * 100 : 0,
          isActive: project?.active ?? false,
          billable: project?.billable ?? false
        };
      })
      .filter(data => !showActiveOnly || data.isActive) // Filter by active status if needed
      .sort((a, b) => b.totalHours - a.totalHours) // Sort by hours descending
      .slice(0, maxProjects); // Take top N projects

    return {
      projects: projectData,
      totalHours,
      hasData: projectData.length > 0
    };
  }, [timesheets, currentEmployee.id, projects, timePeriod, maxProjects, showActiveOnly]);

  const timePeriodLabels: Record<TimePeriod, string> = {
    last30days: 'Last 30 Days',
    last90days: 'Last 90 Days',
    currentMonth: 'Current Month',
    currentQuarter: 'Current Quarter',
    yearToDate: 'Year to Date',
    allTime: 'All Time'
  };

  const formatHours = (hours: number): string => {
    if (hours >= 8) {
      const days = Math.floor(hours / 8);
      const remainingHours = hours % 8;
      if (remainingHours === 0) {
        return `${days}d`;
      }
      return `${days}d ${remainingHours.toFixed(1)}h`;
    }
    return `${hours.toFixed(1)}h`;
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Top Projects by Time
            </CardTitle>
            <CardDescription className="mt-1.5">
              Your personal time investment ranked by hours
            </CardDescription>
          </div>
          <Select value={timePeriod} onValueChange={(value) => setTimePeriod(value as TimePeriod)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last30days">Last 30 Days</SelectItem>
              <SelectItem value="last90days">Last 90 Days</SelectItem>
              <SelectItem value="currentMonth">Current Month</SelectItem>
              <SelectItem value="currentQuarter">Current Quarter</SelectItem>
              <SelectItem value="yearToDate">Year to Date</SelectItem>
              <SelectItem value="allTime">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {!projectTimeData.hasData ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              No approved time entries found for {timePeriodLabels[timePeriod].toLowerCase()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Start logging time to see your top projects
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Total Hours Summary */}
            <div className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Total Time Logged</span>
              </div>
              <span className="text-lg font-bold">{formatHours(projectTimeData.totalHours)}</span>
            </div>

            {/* Project List */}
            <div className="space-y-3">
              {projectTimeData.projects.map((project, index) => (
                <div
                  key={project.projectId}
                  className="group relative"
                >
                  {/* Project Header */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-muted-foreground/60 w-6">
                          #{index + 1}
                        </span>
                        <button
                          onClick={() => onNavigateToProject?.(project.projectId)}
                          className="text-sm font-semibold hover:text-primary transition-colors text-left truncate group/link"
                          title={project.projectName}
                        >
                          {project.projectName}
                          <ExternalLink className="inline-block h-3 w-3 ml-1 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-1 ml-8">
                        <span className="text-xs text-muted-foreground font-mono">
                          {project.projectCode}
                        </span>
                        {project.billable && (
                          <Badge variant="secondary" className="text-xs px-1.5 py-0">
                            Billable
                          </Badge>
                        )}
                        {!project.isActive && (
                          <Badge variant="outline" className="text-xs px-1.5 py-0">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold">
                        {formatHours(project.totalHours)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {project.percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="ml-8">
                    <div className="h-2 bg-accent rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-500 ease-out"
                        style={{ width: `${project.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>Updated {format(lastUpdated, 'MMM d, yyyy h:mm a')}</span>
              </div>
              {projectTimeData.projects.length >= maxProjects && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-auto py-1 px-2"
                  onClick={() => onNavigateToProject?.('all')}
                >
                  View All Projects
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
